import React, { useState } from 'react'
import {
  audienceTitle,
  audiencesForStage,
  choiceLabel,
  collectStageFileTypeSlices,
  emptyProfile,
  filterStageRulesByFileType,
  fileTypeLabels,
  getChoiceItems,
  groupRowsByStage,
  mergeRuleAccess,
  normalizeStageRuleEntries,
  isRowProfilePickable,
  profileOptionsForStage,
  profileTitle,
  ruleMatchesProfile,
  toggleRowInProfile,
  ruleNumbers,
  rulesMatchingAudience,
  rulesMatchingProfile,
  subjectLabel,
} from './fileAccessAdminShared'

const PERM_KEYS = [
  ['read', 'Чтение'],
  ['upload', 'Загрузка'],
  ['view', 'Просмотр'],
  ['download', 'Скачивание'],
  ['delete', 'Удаление'],
]

const AUDIENCE_ICONS = {
  all: 'ti-users',
  role: 'ti-user',
  responsible: 'ti-user-check',
  route_approver: 'ti-route',
  uploader: 'ti-upload',
}

function PermIcons({ access, strip }) {
  return (
    <div className={`fad-perms ${strip ? 'fad-perms--strip' : ''}`}>
      {PERM_KEYS.map(([key, label]) => {
        const on = key === 'read' ? access.read : access[key]
        return (
          <span
            key={key}
            className={`fad-perm ${on ? 'fad-perm--on' : 'fad-perm--off'}`}
          >
            <i
              className={`ti ti-xs ${on ? 'ti-check text-success' : 'ti-x text-muted'}`}
              aria-hidden
            />
            <span className="fad-perm__label">{label}</span>
          </span>
        )
      })}
    </div>
  )
}

function FieldGrid({ rows }) {
  return (
    <dl className="fad-fields mb-0">
      {rows.map(([label, value]) => (
        <div key={label} className="fad-fields__row">
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function SourceRefs({ nums }) {
  if (!nums.length) return <span className="text-muted">—</span>
  return (
    <span className="fad-ref-badges">
      {nums.map((ref) => (
        <span key={ref} className="fad-ref-badge">{ref}</span>
      ))}
    </span>
  )
}

function MergeConnector() {
  return (
    <div className="fad-connector">
      <div className="fad-connector__track">
        <span className="fad-connector__line" aria-hidden />
      </div>
      <div className="fad-connector__gate">
        <i className="ti ti-git-merge fad-connector__gate-icon" aria-hidden />
        <span className="fad-connector__gate-label">ИЛИ</span>
        <span className="fad-connector__gate-hint">подходящие строки суммируются</span>
      </div>
      <div className="fad-connector__track">
        <span className="fad-connector__line" aria-hidden />
      </div>
    </div>
  )
}

function RuleDiagramNode({ entry, roleItems, fileTypeItems, matched, pickable, onToggle }) {
  const row = entry.row || {}
  const num = typeof entry.idx === 'number' ? entry.idx + 1 : '?'
  const subject = row.subject || 'all'
  const alwaysOn = subject === 'all'
  const roles = subject === 'role' ? (row.roles_lnk || []) : []
  const types = fileTypeLabels(row.file_types_lnk, fileTypeItems)
  const access = mergeRuleAccess([entry])

  const audience = (
    <>
      {subjectLabel(subject)}
      {roles.length > 0 && (
        <span className="text-muted">
          {' '}
          (
          {roles.map((id) => choiceLabel(roleItems, id)).join(', ')}
          )
        </span>
      )}
    </>
  )

  const fields = [
    ['Кому', audience],
    ['Типы файлов', types.join(', ')],
    ['Права', <PermIcons access={access} strip />],
  ]
  if (access.read) {
    fields.push(['Список', <span className="text-muted">{access.filesScopeLabel}</span>])
  }

  const stateClass = alwaysOn
    ? ' fad-node--always'
    : matched === true
      ? ' fad-node--match'
      : matched === false
        ? ' fad-node--skip'
        : ''
  const pickableClass = pickable ? ' fad-node--pickable' : ''

  const handleKeyDown = (e) => {
    if (!pickable || !onToggle) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggle()
    }
  }

  return (
    <div
      className={`fad-node card mb-0${stateClass}${pickableClass}`}
      role={pickable ? 'button' : undefined}
      tabIndex={pickable ? 0 : undefined}
      aria-pressed={pickable ? !!matched : undefined}
      onClick={pickable ? onToggle : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className="card-header py-2 px-3 fad-node__header">
        <span className="fad-node__idx">{num}</span>
        <span className="fad-node__title">Строка #{num}</span>
      </div>
      <div className="card-body py-2 px-3">
        <FieldGrid rows={fields} />
      </div>
    </div>
  )
}

function ProfileOutcomeNode({ profile, stageRules, roleItems, fileTypeKey }) {
  const filtered = filterStageRulesByFileType(stageRules, fileTypeKey)
  const matched = rulesMatchingProfile(filtered, profile)
  const access = mergeRuleAccess(matched.map((row) => ({ row })))
  const nums = ruleNumbers(
    normalizeStageRuleEntries(filtered).filter(({ row }) => matched.includes(row))
  )
  const title = profileTitle(profile, roleItems)

  const fields = [
    ['Права', <PermIcons access={access} strip />],
  ]
  if (access.read) {
    fields.push(['В списке', <span className="text-muted">{access.filesScopeLabel}</span>])
  }
  fields.push(['Строки', <SourceRefs nums={nums} />])

  return (
    <div className="fad-node card mb-0 fad-node--profile">
      <div className="card-header py-2 px-3 fad-node__header">
        <span className="fad-node__idx fad-node__idx--icon">
          <i className="ti ti-user-circle" aria-hidden />
        </span>
        <span className="fad-node__title">{title}</span>
      </div>
      <div className="card-body py-2 px-3">
        <FieldGrid rows={fields} />
      </div>
    </div>
  )
}

function OutcomeDiagramNode({ audience, stageRules, roleItems, fileTypeKey }) {
  const filtered = filterStageRulesByFileType(stageRules, fileTypeKey)
  const matched = rulesMatchingAudience(filtered, audience)
  const access = mergeRuleAccess(matched.map((row) => ({ row })))
  const nums = ruleNumbers(
    normalizeStageRuleEntries(filtered).filter(({ row }) => matched.includes(row))
  )
  const title = audienceTitle(audience, roleItems)
  const icon = AUDIENCE_ICONS[audience.type] || 'ti-user'

  const fields = [
    ['Права', <PermIcons access={access} strip />],
  ]
  if (access.read) {
    fields.push(['В списке', <span className="text-muted">{access.filesScopeLabel}</span>])
  }
  fields.push(['Строки', <SourceRefs nums={nums} />])

  return (
    <div className="fad-node card mb-0">
      <div className="card-header py-2 px-3 fad-node__header">
        <span className="fad-node__idx fad-node__idx--icon">
          <i className={`ti ${icon}`} aria-hidden />
        </span>
        <span className="fad-node__title">{title}</span>
      </div>
      <div className="card-body py-2 px-3">
        <FieldGrid rows={fields} />
      </div>
    </div>
  )
}

function ProfileHint({ hasPickableRows }) {
  return (
    <div className="fad-profile-hint">
      {hasPickableRows
        ? 'Нажмите на строку слева, чтобы собрать профиль. Строка «Все» учитывается всегда.'
        : 'На этапе только правило «Все» — итог не зависит от профиля.'}
    </div>
  )
}

function ViewModeToggle({ mode, onChange }) {
  return (
    <div className="fad-mode-toggle" role="group" aria-label="Режим схемы">
      <button
        type="button"
        className={`fad-mode-toggle__btn${mode === 'audiences' ? ' fad-mode-toggle__btn--active' : ''}`}
        aria-pressed={mode === 'audiences'}
        onClick={() => onChange('audiences')}
      >
        Аудитории
      </button>
      <button
        type="button"
        className={`fad-mode-toggle__btn${mode === 'profile' ? ' fad-mode-toggle__btn--active' : ''}`}
        aria-pressed={mode === 'profile'}
        onClick={() => onChange('profile')}
      >
        Профиль
      </button>
    </div>
  )
}

function FlowColumnHeader({ title, icon, count, column }) {
  return (
    <div className={`fad-flow__col-head fad-flow__col-head--${column}`}>
      <div className="fad-flow__col-head-inner">
        <i className={`ti ${icon} fad-flow__col-icon`} aria-hidden />
        <span className="fad-flow__col-title card-action-title">{title}</span>
        {count != null && <span className="fad-panel__count">{count}</span>}
      </div>
    </div>
  )
}

function computeProfileGridBlocks(entries, fileTypeSlices, multiFileTypes, firstDataRow) {
  const blocks = []
  let row = firstDataRow
  fileTypeSlices.forEach((slice, sliceIndex) => {
    if (multiFileTypes) {
      blocks.push({ kind: 'slice-title', gridRow: row, slice, key: `slice-${slice.key || 'all'}` })
      row += 1
    }
    blocks.push({
      kind: 'profile-block',
      key: `profile-${slice.key || 'all'}`,
      gridRowStart: row,
      gridRowEnd: row + entries.length,
      slice,
      sliceIndex,
    })
    row += entries.length
  })
  return blocks
}

function buildAlignedFlowRows(entries, audiences, fileTypeSlices, multiFileTypes) {
  const rowCount = Math.max(entries.length, audiences.length)
  const rows = []
  fileTypeSlices.forEach((slice, sliceIndex) => {
    if (multiFileTypes) {
      rows.push({ kind: 'slice-title', key: `slice-${slice.key || 'all'}`, slice })
    }
    for (let i = 0; i < rowCount; i += 1) {
      rows.push({
        kind: 'pair',
        key: `pair-${slice.key || 'all'}-${i}`,
        slice,
        sliceIndex,
        index: i,
        rule: sliceIndex === 0 ? entries[i] : null,
        audience: audiences[i],
      })
    }
  })
  return rows
}

function StageFlowDiagram({ group, stageItems, roleItems, fileTypeItems }) {
  const [viewMode, setViewMode] = useState('audiences')
  const [profile, setProfile] = useState(emptyProfile)

  const stageTitle = choiceLabel(stageItems, group.stage)
  const entries = group.rules.filter((r) => (r.row || {}).stage)
  if (entries.length === 0) {
    return <div className="text-muted small">Укажите этап в строках таблицы.</div>
  }

  const audiences = audiencesForStage(entries)
  const fileTypeSlices = collectStageFileTypeSlices(entries, fileTypeItems)
  const multiFileTypes = fileTypeSlices.length > 1
  const {
    roleIds,
    hasResponsible,
    hasUploader,
    hasRouteApprover,
  } = profileOptionsForStage(entries)
  const hasPickableRows = roleIds.length > 0
    || hasResponsible
    || hasUploader
    || hasRouteApprover
  const isProfileMode = viewMode === 'profile'

  const toggleProfileRow = (row) => {
    setProfile((prev) => toggleRowInProfile(row, prev))
  }
  const outcomeCount = isProfileMode
    ? fileTypeSlices.length
    : audiences.length * fileTypeSlices.length
  const flowRows = buildAlignedFlowRows(entries, audiences, fileTypeSlices, multiFileTypes)
  const profileBlocks = computeProfileGridBlocks(entries, fileTypeSlices, multiFileTypes, 2)
  const firstDataRow = 2
  const lastDataRow = isProfileMode
    ? (profileBlocks.length > 0 ? profileBlocks[profileBlocks.length - 1].gridRowEnd : firstDataRow)
    : firstDataRow + flowRows.length - 1

  return (
    <div className="fad-stage card mb-3">
      <div className="card-header py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="card-action-title mb-0">
          <i className="ti ti-git-branch me-1" aria-hidden />
          Этап:
          {' '}
          {stageTitle}
        </span>
        <ViewModeToggle mode={viewMode} onChange={setViewMode} />
      </div>
      <div className="card-body p-3">
        {isProfileMode && <ProfileHint hasPickableRows={hasPickableRows} />}
        <div className="fad-flow">
          <FlowColumnHeader
            column="rules"
            title="Строки таблицы"
            icon="ti-table"
            count={entries.length}
          />
          <div className="fad-flow__col-head fad-flow__col-head--mid" aria-hidden />
          <FlowColumnHeader
            column="outcomes"
            title={isProfileMode ? 'Итог для профиля' : 'Итог на этапе'}
            icon={isProfileMode ? 'ti-user-circle' : 'ti-users'}
            count={outcomeCount}
          />

          {isProfileMode ? (
            profileBlocks.map((block) => {
              if (block.kind === 'slice-title') {
                return (
                  <div
                    key={block.key}
                    className="fad-flow__slice-title"
                    style={{ gridRow: block.gridRow }}
                  >
                    <i className="ti ti-file-description me-1" aria-hidden />
                    {block.slice.label}
                  </div>
                )
              }

              const { gridRowStart, gridRowEnd, slice, sliceIndex } = block
              return (
                <React.Fragment key={block.key}>
                  {entries.map((entry, i) => {
                    const row = entry.row || {}
                    const matched = ruleMatchesProfile(row, profile)
                    return (
                      <div
                        key={`${block.key}-rule-${i}`}
                        className="fad-flow__cell fad-flow__cell--rule"
                        style={{ gridRow: gridRowStart + i }}
                      >
                        {sliceIndex === 0 ? (
                          <RuleDiagramNode
                            entry={entry}
                            roleItems={roleItems}
                            fileTypeItems={fileTypeItems}
                            matched={matched}
                            pickable={isRowProfilePickable(row)}
                            onToggle={() => toggleProfileRow(row)}
                          />
                        ) : (
                          <div className="fad-flow__cell-placeholder" aria-hidden />
                        )}
                      </div>
                    )
                  })}
                  <div
                    className="fad-flow__gate"
                    style={{ gridRow: `${gridRowStart} / ${gridRowEnd}` }}
                  >
                    <MergeConnector />
                  </div>
                  <div
                    className="fad-flow__cell fad-flow__cell--outcome fad-flow__cell--profile-outcome"
                    style={{ gridRow: `${gridRowStart} / ${gridRowEnd}` }}
                  >
                    <ProfileOutcomeNode
                      profile={profile}
                      stageRules={entries}
                      roleItems={roleItems}
                      fileTypeKey={slice.key}
                    />
                  </div>
                </React.Fragment>
              )
            })
          ) : (
            <>
              {flowRows.length > 0 && (
                <div
                  className="fad-flow__gate"
                  style={{ gridRow: `${firstDataRow} / ${lastDataRow + 1}` }}
                >
                  <MergeConnector />
                </div>
              )}

              {flowRows.map((row, idx) => {
                const gridRow = firstDataRow + idx
                if (row.kind === 'slice-title') {
                  return (
                    <div
                      key={row.key}
                      className="fad-flow__slice-title"
                      style={{ gridRow }}
                    >
                      <i className="ti ti-file-description me-1" aria-hidden />
                      {row.slice.label}
                    </div>
                  )
                }

                return (
                  <div key={row.key} className="fad-flow__pair" style={{ gridRow }}>
                    <div className="fad-flow__cell fad-flow__cell--rule">
                      {row.rule ? (
                        <RuleDiagramNode
                          entry={row.rule}
                          roleItems={roleItems}
                          fileTypeItems={fileTypeItems}
                        />
                      ) : (
                        <div className="fad-flow__cell-placeholder" aria-hidden />
                      )}
                    </div>
                    <div className="fad-flow__cell fad-flow__cell--outcome">
                      {row.audience ? (
                        <OutcomeDiagramNode
                          audience={row.audience}
                          stageRules={entries}
                          roleItems={roleItems}
                          fileTypeKey={row.slice.key}
                        />
                      ) : (
                        <div className="fad-flow__cell-placeholder" aria-hidden />
                      )}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function FileAccessPreview({ rows, cardFlow, choices, attrs }) {
  const stageItems = getChoiceItems(choices, 'FileAccessProjectState')
  const roleItems = getChoiceItems(choices, 'Role')
  const flowItems = getChoiceItems(choices, 'DocumentTypeFlow')
  const stageTableMeta = attrs?.stage_access_links_lnk || {}
  const fileTypeItems = (cardFlow && stageTableMeta.flow_file_types?.[cardFlow]) || []
  const flowLabel = cardFlow ? choiceLabel(flowItems, cardFlow) : null
  const groups = groupRowsByStage(rows, cardFlow)
  const validRows = (rows || []).filter((r) => r && r.stage)

  if (!cardFlow) {
    return (
      <div className="file-access-preview text-muted small py-2">
        Выберите документооборот — здесь появится схема прав.
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="file-access-preview text-muted small py-2">
        Добавьте правила в таблицу — схема обновится автоматически.
      </div>
    )
  }

  return (
    <div className="file-access-preview file-access-diagram">
      <div className="mb-3">
        <h6 className="mb-1">Схема доступа</h6>
        <div className="small text-muted">
          Документооборот:
          {' '}
          <strong>{flowLabel || cardFlow}</strong>
          {validRows.length < rows.length && (
            <span className="ms-2 text-warning">
              (
              {rows.length - validRows.length}
              {' '}
              без этапа)
            </span>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-muted small">Укажите этап хотя бы в одной строке.</div>
      ) : (
        groups.map((group) => (
          <StageFlowDiagram
            key={group.stage}
            group={group}
            stageItems={stageItems}
            roleItems={roleItems}
            fileTypeItems={fileTypeItems}
          />
        ))
      )}
    </div>
  )
}
